import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, HelpCircle, X, GripVertical } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyFaq = { question: '', answer: '', category: 'General', order: 0, is_active: true };

const CATEGORIES = ['General', 'Booking', 'Payments', 'Cancellation', 'Visa', 'Travel Tips'];

export default function AdminFAQs() {
  const { token } = useAdmin();
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState(emptyFaq);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterCat, setFilterCat] = useState('All');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/faqs`, { headers });
    setFaqs(data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.question || !form.answer) return toast.error('Question and answer required');
    try {
      if (editing) {
        await axios.put(`${API}/admin/faqs/${editing}`, form, { headers });
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API}/admin/faqs`, { ...form, order: faqs.length }, { headers });
        toast.success('FAQ created');
      }
      setShowForm(false); setEditing(null); setForm(emptyFaq); load();
    } catch (e) { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    await axios.delete(`${API}/admin/faqs/${id}`, { headers });
    toast.success('Deleted'); load();
  };

  const edit = (f) => { setForm({ ...f }); setEditing(f.id); setShowForm(true); };
  const toggle = async (f) => {
    await axios.put(`${API}/admin/faqs/${f.id}`, { is_active: !f.is_active }, { headers });
    load();
  };

  const filtered = filterCat === 'All' ? faqs : faqs.filter(f => f.category === filterCat);
  const categories = [...new Set(faqs.map(f => f.category))];

  return (
    <div className="space-y-6" data-testid="admin-faqs">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">FAQ Manager</h1>
          <p className="text-slate-500 text-sm mt-1">Manage frequently asked questions shown on the website</p>
        </div>
        <Button onClick={() => { setForm(emptyFaq); setEditing(null); setShowForm(true); }} data-testid="add-faq-btn">
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 flex-wrap">
        {['All', ...CATEGORIES].map(c => (
          <Button key={c} variant={filterCat === c ? 'default' : 'outline'} size="sm" onClick={() => setFilterCat(c)}>{c}</Button>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editing ? 'Edit' : 'Add'} FAQ</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Category</Label>
                <select className="w-full border rounded-md px-3 py-2 text-sm mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="faq-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><Label>Question</Label><Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question" /></div>
              <div><Label>Answer</Label><Textarea value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} rows={4} data-testid="faq-answer" /></div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Active</Label>
              </div>
              <Button className="w-full" onClick={save} data-testid="save-faq-btn">{editing ? 'Update' : 'Create'} FAQ</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {filtered.length === 0 && <p className="text-slate-500 text-center py-8">No FAQs yet. Add your first question!</p>}
        {filtered.map((f, i) => (
          <Card key={f.id} className={`${!f.is_active ? 'opacity-60' : ''}`} data-testid={`faq-card-${f.id}`}>
            <CardContent className="py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <HelpCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">{f.question}</p>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{f.answer}</p>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded mt-2 inline-block">{f.category}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Switch checked={f.is_active} onCheckedChange={() => toggle(f)} />
                  <Button variant="ghost" size="icon" onClick={() => edit(f)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(f.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
